import '@polymer/paper-input/paper-input.js';
import '@polymer/paper-input/paper-textarea.js';
import '@polymer/paper-checkbox/paper-checkbox.js';
import '../katapult-elements/katapult-dialog.js';
import '../katapult-elements/katapult-button.js';
import { html } from '@polymer/polymer/lib/utils/html-tag.js';
import { PolymerElement } from '@polymer/polymer/polymer-element.js';
import firebase from 'firebase/compat/app';
import 'firebase/compat/storage';
import {
  buildBugFeedbackBody,
  getDefaultReporterName,
  isInternalBugReporter,
  sendBugFeedbackRequest,
  uploadBugScreenshots,
  validateBugScreenshotFile
} from '../../modules/BugFeedback.js';

class BugFeedbackDialog extends PolymerElement {
  static get template() {
    return html`
      <style>
        #imgUploadTrigger {
          position: relative;
          width: 95%;
        }

        #imgUploadLabel {
          display: block;
          background-color: var(--secondary-color);
          border: 2px solid var(--secondary-color);
          text-align: center;
          color: white;
          padding: 8px;
          border-radius: 15px;
          width: 100%;
          font-size: 16px;
          font-family: inherit;
          box-sizing: border-box;
          cursor: pointer;
        }

        #imgUpload {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          opacity: 0;
          cursor: pointer;
        }

        .errorMessage {
          color: var(--paper-red-500, #f44336);
        }

        #bugFeedbackDialog paper-input,
        #bugFeedbackDialog paper-textarea {
          --paper-input-container-focus-color: var(--secondary-color);
        }

        #bugFeedbackDialog paper-checkbox {
          --paper-checkbox-unchecked-color: var(--primary-text-color-faded);
          --paper-checkbox-checked-color: var(--secondary-color);
        }

        .content {
          max-width: 800px;
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        #screenshotUploadContainer {
          width: 100%;
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
        }

        #screenshotList {
          width: 95%;
          margin-top: 8px;
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .screenshotRow {
          display: flex;
          align-items: center;
          font-size: 14px;
          color: var(--primary-text-color);
          padding: 2px 8px;
          border-radius: 2px;
        }

        .screenshotRow:hover {
          background-color: rgba(0, 0, 0, 0.06);
        }

        .screenshotRow > span {
          flex-grow: 1;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          margin-right: 4px;
        }
      </style>

      <katapult-dialog
        id="bugFeedbackDialog"
        title="Software Bug Feedback"
        icon="bug_report"
        entry-animation="scale-up-animation"
        exit-animation="fade-out-animation"
        no-cancel-on-esc-key
        no-cancel-on-outside-click
      >
        <div class="content">
          <div hidden$="[[!hasUserEmail(userEmail)]]">
            Your email address (<strong>[[userEmail]]</strong>) will be recorded with this report.<br />Katapult Pro Support will have
            temporary access to your company data, and will contact you to help resolve the issue.
          </div>
          <div hidden$="[[hasUserEmail(userEmail)]]">
            Katapult Pro Support will have access to your company data, and will contact you to help resolve the issue.
          </div>
          <paper-textarea
            id="bugSubject"
            label="Subject"
            value="{{bugSubject}}"
            placeholder="Subject of the bug"
            error-message="Please enter a subject."
            always-float-label
          ></paper-textarea>
          <paper-textarea
            id="bugDescription"
            label="What went wrong?"
            value="{{bugEncountered}}"
            placeholder="Bug you encountered"
            error-message="Please enter the bug you encountered."
            always-float-label
            required
            autofocus
          ></paper-textarea>
          <paper-textarea
            id="bugRecreation"
            label="How can the bug be recreated?"
            value="{{bugRecreation}}"
            placeholder="Recreation instructions"
            always-float-label
          ></paper-textarea>
          <paper-textarea
            id="bugExpected"
            label="What should the feature do?"
            value="{{bugExpected}}"
            placeholder="Expected behavior"
            error-message="Please tell us what it's supposed to do."
            always-float-label
            required
            hidden$="[[!isInternalUser(userEmail)]]"
          ></paper-textarea>
          <paper-input
            id="yourName"
            label="What's your name?"
            value="{{yourName}}"
            error-message="Please enter your name."
            placeholder="Name"
            always-float-label
          ></paper-input>
          <div id="screenshotUploadContainer">
            <div id="imgUploadTrigger" aria-label="Attach screenshots of the bug (optional)">
              <label id="imgUploadLabel" for="imgUpload">Attach screenshots of the bug (optional)</label>
              <input id="imgUpload" accept="*/*" name="imgUpload" type="file" on-change="verifyFileUpload" />
            </div>
            <template is="dom-if" if="[[screenshotFiles.length]]">
              <div id="screenshotList">
                <template is="dom-repeat" items="[[screenshotFiles]]">
                  <div class="screenshotRow">
                    <span title="[[item.name]]">[[item.name]]</span>
                    <katapult-button icon="close" iconOnly noBorder size="18" on-click="removeScreenshot"></katapult-button>
                  </div>
                </template>
              </div>
            </template>
          </div>
          <paper-checkbox checked="{{sendToMe}}" hidden$="[[!hasUserEmail(userEmail)]]">Send me a copy of my report</paper-checkbox>
          <div class="errorMessage" hidden$="[[!submittedError]]">[[submittedError]]</div>
          <h3 hidden$="[[!submittedFeedback]]">
            Thanks for your feedback! We will review the issue and get back to you as soon as we can.
          </h3>
        </div>

        <div slot="buttons">
          <katapult-button style="margin-right: 8px;" dialog-dismiss on-click="clearBugFeedback" hidden$="[[submittedFeedback]]"
            >Cancel</katapult-button
          >
          <katapult-button
            color="var(--secondary-color)"
            loading="[[submitting]]"
            on-click="submitBugFeedback"
            hidden$="[[submittedFeedback]]"
            >Submit</katapult-button
          >
          <katapult-button noBorder dialog-dismiss on-click="clearBugFeedback" hidden$="[[!submittedFeedback]]">Close</katapult-button>
        </div>
      </katapult-dialog>
    `;
  }

  static get properties() {
    return {
      userEmail: { type: String, value: '' },
      userDisplayName: { type: String, value: '' },
      databaseName: { type: String, value: '' },
      modelName: { type: String, value: '' },
      jobId: { type: String, value: '' },
      selectedNode: { type: String, value: '' },
      activeConnection: { type: String, value: '' },
      submittedFeedback: { type: Boolean, value: false },
      submitting: { type: Boolean, value: false },
      sendToMe: { type: Boolean, value: false },
      bugSubject: { type: String, value: '' },
      bugEncountered: { type: String, value: '' },
      bugRecreation: { type: String, value: '' },
      bugExpected: { type: String, value: '' },
      yourName: { type: String, value: '' },
      reporterEmail: { type: String, value: '' },
      submittedError: { type: String, value: '' },
      screenshotFiles: { type: Array, value: () => [] }
    };
  }

  isInternalUser(email) {
    return isInternalBugReporter(email);
  }

  hasUserEmail(email) {
    return Boolean(email);
  }

  open() {
    const defaultEmail = this.userEmail || '';
    this.reporterEmail = defaultEmail;
    this.yourName = getDefaultReporterName({ userDisplayName: this.userDisplayName });
    this.$.bugFeedbackDialog.open();
  }

  verifyFileUpload(event) {
    const uploadElement = event?.target || this.shadowRoot.querySelector('#imgUpload');
    const selectedFiles = Array.from(uploadElement?.files || []);
    const invalidNames = [];

    for (const file of selectedFiles) {
      const validation = validateBugScreenshotFile(file);
      if (!validation.valid) {
        invalidNames.push(file.name);
        continue;
      }
      const isDuplicate = this.screenshotFiles.some((existing) => existing.name === file.name && existing.size === file.size);
      if (!isDuplicate) this.push('screenshotFiles', file);
    }

    if (uploadElement) uploadElement.value = null;
    if (invalidNames.length) {
      alert(`Invalid file type. Please upload an image file.\n\nSkipped: ${invalidNames.join(', ')}`);
    }
  }

  removeScreenshot(e) {
    this.splice('screenshotFiles', e.model.index, 1);
  }

  clearBugFeedback() {
    this.$.bugFeedbackDialog.close();
    this.bugSubject = '';
    this.bugEncountered = '';
    this.yourName = '';
    this.reporterEmail = '';
    this.bugExpected = '';
    this.bugRecreation = '';
    this.sendToMe = false;

    this.shadowRoot.querySelector('#bugDescription').invalid = false;
    this.shadowRoot.querySelector('#yourName').invalid = false;
    this.shadowRoot.querySelector('#bugSubject').invalid = false;
    if (this.shadowRoot.querySelector('#bugExpected')) this.shadowRoot.querySelector('#bugExpected').invalid = false;
    if (this.shadowRoot.querySelector('#bugRecreation')) this.shadowRoot.querySelector('#bugRecreation').invalid = false;
    if (this.shadowRoot.querySelector('#imgUpload')) this.shadowRoot.querySelector('#imgUpload').value = null;
    this.screenshotFiles = [];

    this.submitting = false;
    this.submittedFeedback = false;
    this.submittedError = '';
  }

  async submitBugFeedback() {
    let valid = true;

    this.shadowRoot.querySelector('#bugDescription').invalid = false;
    this.shadowRoot.querySelector('#yourName').invalid = false;

    this.reporterEmail = this.userEmail || '';

    if (!this.bugEncountered) {
      valid = false;
      this.shadowRoot.querySelector('#bugDescription').invalid = true;
    }
    if (!this.yourName) {
      valid = false;
      this.shadowRoot.querySelector('#yourName').invalid = true;
    }

    if (isInternalBugReporter(this.userEmail)) {
      if (this.shadowRoot.querySelector('#bugExpected')) this.shadowRoot.querySelector('#bugExpected').invalid = false;
      if (!this.bugExpected && this.shadowRoot.querySelector('#bugExpected')) {
        valid = false;
        this.shadowRoot.querySelector('#bugExpected').invalid = true;
      }
    }

    if (!valid) return;

    const to = ['katapultpro-bugs@katapultengineering.com'];
    if (this.sendToMe && this.reporterEmail) to.push({ type: 'cc', email: this.reporterEmail });

    this.submitting = true;
    this.submittedError = '';

    try {
      const screenshots = await uploadBugScreenshots({ files: this.screenshotFiles, firebase, FirebaseWorker });
      const body = buildBugFeedbackBody({
        description: this.bugEncountered,
        reporterName: this.yourName,
        reporterEmail: this.reporterEmail,
        pageUrl: window.location.href,
        screenshots,
        bugRecreation: this.bugRecreation,
        bugExpected: this.bugExpected,
        includeInternalFields: isInternalBugReporter(this.userEmail),
        details: {
          Database: this.databaseName,
          Model: this.modelName,
          Job: this.jobId,
          Node: this.selectedNode,
          Connection: this.activeConnection
        }
      });

      const response = await sendBugFeedbackRequest({
        FirebaseWorker,
        addresses: to,
        subject: this.bugSubject,
        body
      });

      this.submittedFeedback = true;
      if (!response.success) {
        this.submittedError = response.message || 'There was an issue submitting your feedback.';
      }
    } catch (error) {
      this.submittedError = 'There was an issue submitting your feedback. Please try again or email support@katapultengineering.com.';
    } finally {
      this.submitting = false;
    }
  }
}

window.customElements.define('bug-feedback-dialog', BugFeedbackDialog);
